import { AddBlocksButton } from '@/features/toolbox/AddBlocksButton';
import { attachTrashPositionSync } from '@/features/workspace/workspaceUiLayout';
import { createBlocklyTheme } from '@/theme/blocklyTheme';
import { useBoardStore } from '@/state/boardStore';
import { useSettingsStore } from '@/state/settingsStore';
import { useToolboxStore } from '@/state/toolboxStore';
import { useWorkspaceStore } from '@/state/workspaceStore';
import {
  attachSkyroverVariableCreatorBar,
  registerAllBlocks,
  registerSkyroverVariableToolbox,
  setActiveBoard,
  SkyroverConnectionChecker,
} from '@skyrover/blockly-blocks';
import { buildToolboxForBoard } from '@skyrover/blockly-toolbox';
import { generateSketch } from '@skyrover/code-generators';
import { WORKSPACE_AUTOSAVE_DEBOUNCE_MS } from '@skyrover/shared/constants';
import { Box } from '@mui/material';
import { inject, serialization, setLocale, WorkspaceSvg } from 'blockly/core';
import * as En from 'blockly/msg/en';
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';

import 'blockly/blocks';

setLocale(En as unknown as { [key: string]: string });

export interface BlocklyWorkspaceHandle {
  loadWorkspaceState: (state: object) => void;
  getWorkspaceState: () => object;
  regenerateCode: () => void;
  clearWorkspace: () => void;
  zoomIn: () => void;
  zoomOut: () => void;
  centerView: () => void;
}

interface BlocklyWorkspaceProps {
  onWorkspaceChange?: () => void;
}

function seedDefaultProgram(workspace: WorkspaceSvg, mode: 'beginner' | 'advanced'): void {
  if (workspace.getTopBlocks(false).length > 0) {
    return;
  }
  const programType = mode === 'beginner' ? 'beginner_start' : 'arduino_program';
  new serialization.blocks.BlockSerializer().load(
    {
      languageVersion: 0,
      blocks: [
        {
          type: programType,
          id: 'default_program',
          x: 40,
          y: 40,
        },
      ],
    },
    workspace,
  );
}

export const BlocklyWorkspace = forwardRef<BlocklyWorkspaceHandle, BlocklyWorkspaceProps>(
  function BlocklyWorkspace({ onWorkspaceChange }, ref) {
    const containerRef = useRef<HTMLDivElement>(null);
    const workspaceRef = useRef<WorkspaceSvg | null>(null);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const board = useBoardStore((s) => s.board);
    const boardId = useBoardStore((s) => s.boardId);
    const blockOperationMode = useSettingsStore((s) => s.blockOperationMode);
    const enabledAddonIds = useToolboxStore((s) => s.enabledAddonIds);
    const setGeneratedCode = useWorkspaceStore((s) => s.setGeneratedCode);
    const markDirty = useWorkspaceStore((s) => s.markDirty);

    const regenerateCode = () => {
      const workspace = workspaceRef.current;
      if (!workspace) {
        return;
      }
      const sketch = generateSketch(workspace, board);
      setGeneratedCode(sketch.code);
    };

    useImperativeHandle(ref, () => ({
      loadWorkspaceState: (state: object) => {
        const workspace = workspaceRef.current;
        if (!workspace) {
          return;
        }
        workspace.clear();
        const blocksState = (state as { blocks?: { languageVersion: number; blocks: unknown[] } }).blocks;
        if (blocksState) {
          new serialization.blocks.BlockSerializer().load(
            blocksState as Parameters<serialization.blocks.BlockSerializer['load']>[0],
            workspace,
          );
        } else {
          seedDefaultProgram(workspace, blockOperationMode);
        }
        regenerateCode();
        markDirty();
        onWorkspaceChange?.();
      },
      getWorkspaceState: () => {
        const workspace = workspaceRef.current;
        if (!workspace) {
          return { blocks: { languageVersion: 0, blocks: [] } };
        }
        return { blocks: new serialization.blocks.BlockSerializer().save(workspace) };
      },
      regenerateCode,
      clearWorkspace: () => {
        const workspace = workspaceRef.current;
        if (!workspace) {
          return;
        }
        workspace.clear();
        seedDefaultProgram(workspace, blockOperationMode);
        regenerateCode();
        markDirty();
      },
      zoomIn: () => workspaceRef.current?.zoomCenter(1),
      zoomOut: () => workspaceRef.current?.zoomCenter(-1),
      centerView: () => workspaceRef.current?.scrollCenter(),
    }));

    useEffect(() => {
      registerAllBlocks();
      setActiveBoard(board);

      if (!containerRef.current || workspaceRef.current) {
        return;
      }

      const toolbox = buildToolboxForBoard(boardId, enabledAddonIds, blockOperationMode);

      const workspace = inject(containerRef.current, {
        toolbox,
        theme: createBlocklyTheme(false),
        plugins: {
          connectionChecker: SkyroverConnectionChecker,
        },
        grid: {
          spacing: 20,
          length: 3,
          colour: '#e0e0e0',
          snap: true,
        },
        zoom: {
          controls: false,
          wheel: true,
          startScale: 1,
          maxScale: 3,
          minScale: 0.3,
        },
        trashcan: true,
        sounds: false,
        renderer: 'zelos',
      }) as WorkspaceSvg;

      workspaceRef.current = workspace;
      registerSkyroverVariableToolbox(workspace);
      const detachVariableCreator = attachSkyroverVariableCreatorBar(containerRef.current, workspace);
      seedDefaultProgram(workspace, blockOperationMode);

      const changeListener = () => {
        if (debounceRef.current) {
          clearTimeout(debounceRef.current);
        }
        debounceRef.current = setTimeout(() => {
          regenerateCode();
          markDirty();
          onWorkspaceChange?.();
        }, WORKSPACE_AUTOSAVE_DEBOUNCE_MS);
      };

      workspace.addChangeListener(changeListener);
      regenerateCode();

      const detachTrashSync = attachTrashPositionSync(containerRef.current);

      return () => {
        detachTrashSync();
        detachVariableCreator();
        if (debounceRef.current) {
          clearTimeout(debounceRef.current);
        }
        workspace.dispose();
        workspaceRef.current = null;
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      setActiveBoard(board);
      regenerateCode();
    }, [board]);

    useEffect(() => {
      const workspace = workspaceRef.current;
      if (!workspace) {
        return;
      }
      workspace.updateToolbox(buildToolboxForBoard(boardId, enabledAddonIds, blockOperationMode));
    }, [boardId, enabledAddonIds, blockOperationMode]);

    return (
      <Box
        ref={containerRef}
        sx={{
          width: '100%',
          height: '100%',
          minWidth: 0,
          minHeight: 0,
          position: 'relative',
        }}
      >
        <AddBlocksButton workspaceContainerRef={containerRef} />
      </Box>
    );
  },
);
